<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePage } from '@inertiajs/vue3'
import UiButton from '@/Components/UI/UiButton.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'
import { useInboxStore } from '@/stores/useInboxStore'

const props = defineProps({
  conversationId: { type: Number, required: true },
  notes:          { type: Array,  default: () => [] },
  taken:          { type: Boolean, default: false },
})

const { t } = useI18n()
const store = useInboxStore()
const page = usePage()
const body = ref('')
const sending = ref(false)
const currentUser = computed(() => page.props.auth?.user)

const canDelete = (note) =>
  currentUser.value &&
  (currentUser.value.role === 'admin' || currentUser.value.id === note.author_id)

const submit = async () => {
  if (!body.value.trim() || sending.value) return
  sending.value = true
  try {
    const result = await store.addNote(props.conversationId, body.value)
    if (result) body.value = ''
  } finally {
    sending.value = false
  }
}

const remove = (noteId) => store.removeNote(props.conversationId, noteId)

const formatTs = (iso) => {
  if (!iso) return ''
  return new Date(iso).toLocaleString()
}
</script>

<template>
  <section class="rounded-[var(--radius-lg)] border border-[color:var(--line)] p-3">
    <h3 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-2">
      {{ t('inbox.notes.title') }}
    </h3>
    <ul
      v-if="notes.length"
      class="space-y-2 mb-3 max-h-60 overflow-y-auto"
    >
      <li
        v-for="n in notes"
        :key="n.id"
        class="rounded p-2 text-xs"
        :style="{ background: 'var(--surfaceAlt)' }"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="fw-value text-[color:var(--ink)]">{{ n.author_name || '—' }}</span>
          <span class="text-[10px] text-[color:var(--muted)]">{{ formatTs(n.created_at) }}</span>
        </div>
        <p
          class="mt-1 whitespace-pre-wrap text-[color:var(--ink)]"
          :class="n.status === 'sending' ? 'opacity-60' : ''"
        >
          {{ n.body }}
        </p>
        <button
          v-if="canDelete(n)"
          type="button"
          class="mt-1 text-[10px] text-[color:var(--muted)] hover:text-[color:var(--danger)]"
          @click="remove(n.id)"
        >
          {{ t('inbox.notes.delete') }}
        </button>
      </li>
    </ul>
    <p
      v-else
      class="text-xs text-[color:var(--muted)] mb-2"
    >
      {{ t('inbox.notes.empty') }}
    </p>

    <form
      v-if="props.taken"
      class="space-y-2"
      @submit.prevent="submit"
    >
      <UiTextarea
        v-model="body"
        rows="2"
        :placeholder="t('inbox.notes.placeholder')"
      />
      <UiButton
        type="submit"
        variant="secondary"
        size="sm"
        :loading="sending"
        :disabled="!body.trim()"
      >
        {{ t('inbox.notes.add') }}
      </UiButton>
    </form>
    <p
      v-else
      class="text-xs text-[color:var(--muted)]"
    >
      {{ t('inbox.thread.takeFirst') }}
    </p>
  </section>
</template>
